
import { useState, FormEvent } from 'react';

export default function Contact() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    revenue: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5b0fitcrgmf5papdbog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          revenue: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#0F3A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Schedule Your Free Strategy Call
          </h2>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            Let's discuss how we can help transform your business. No pressure, just honest conversation about your goals and challenges.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden shadow-2xl">
          <div className="bg-gray-50 p-10 lg:p-12">
            <form onSubmit={handleSubmit} data-readdy-form id="hvac-contact-form">
              <div className="space-y-8">
                <div className="relative">
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="peer w-full px-0 py-4 bg-transparent border-0 border-b-2 border-gray-300 focus:border-[#0F3A2E] focus:ring-0 text-base text-gray-900 placeholder-transparent transition-colors"
                    placeholder="Full Name"
                  />
                  <label
                    htmlFor="name"
                    className="absolute left-0 -top-3.5 text-gray-600 text-sm transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-4 peer-focus:-top-3.5 peer-focus:text-[#0F3A2E] peer-focus:text-sm"
                  >
                    Full Name *
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="peer w-full px-0 py-4 bg-transparent border-0 border-b-2 border-gray-300 focus:border-[#0F3A2E] focus:ring-0 text-base text-gray-900 placeholder-transparent transition-colors"
                    placeholder="Email Address"
                  />
                  <label
                    htmlFor="email"
                    className="absolute left-0 -top-3.5 text-gray-600 text-sm transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-4 peer-focus:-top-3.5 peer-focus:text-[#0F3A2E] peer-focus:text-sm"
                  >
                    Email Address *
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="peer w-full px-0 py-4 bg-transparent border-0 border-b-2 border-gray-300 focus:border-[#0F3A2E] focus:ring-0 text-base text-gray-900 placeholder-transparent transition-colors"
                    placeholder="Phone Number"
                  />
                  <label
                    htmlFor="phone"
                    className="absolute left-0 -top-3.5 text-gray-600 text-sm transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-4 peer-focus:-top-3.5 peer-focus:text-[#0F3A2E] peer-focus:text-sm"
                  >
                    Phone Number *
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="text"
                    id="company"
                    name="company"
                    required
                    value={formData.company}
                    onChange={handleChange}
                    className="peer w-full px-0 py-4 bg-transparent border-0 border-b-2 border-gray-300 focus:border-[#0F3A2E] focus:ring-0 text-base text-gray-900 placeholder-transparent transition-colors"
                    placeholder="Company Name"
                  />
                  <label
                    htmlFor="company"
                    className="absolute left-0 -top-3.5 text-gray-600 text-sm transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-4 peer-focus:-top-3.5 peer-focus:text-[#0F3A2E] peer-focus:text-sm"
                  >
                    Company Name *
                  </label>
                </div>

                <div className="relative">
                  <select
                    id="revenue"
                    name="revenue"
                    required
                    value={formData.revenue}
                    onChange={handleChange}
                    className="peer w-full px-0 py-4 bg-transparent border-0 border-b-2 border-gray-300 focus:border-[#0F3A2E] focus:ring-0 text-base text-gray-900 transition-colors"
                  >
                    <option value="">Select range</option>
                    <option value="under-250k">Under $250K</option>
                    <option value="250k-500k">$250K - $500K</option>
                    <option value="500k-1m">$500K - $1M</option>
                    <option value="1m-2m">$1M - $2M</option>
                    <option value="over-2m">Over $2M</option>
                  </select>
                  <label
                    htmlFor="revenue"
                    className="absolute left-0 -top-3.5 text-gray-600 text-sm"
                  >
                    Annual Revenue Range *
                  </label>
                </div>

                <div className="relative">
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    value={formData.message}
                    onChange={handleChange}
                    className="peer w-full px-0 py-4 bg-transparent border-0 border-b-2 border-gray-300 focus:border-[#0F3A2E] focus:ring-0 text-base text-gray-900 placeholder-transparent resize-none transition-colors"
                    placeholder="Tell Us About Your Goals"
                  />
                  <label
                    htmlFor="message"
                    className="absolute left-0 -top-3.5 text-gray-600 text-sm transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-4 peer-focus:-top-3.5 peer-focus:text-[#0F3A2E] peer-focus:text-sm"
                  >
                    Tell Us About Your Goals *
                  </label>
                  <p className="text-xs text-gray-500 mt-2">{formData.message.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-[#0F3A2E] text-white font-semibold rounded-full hover:bg-[#0A2A20] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-lg shadow-lg"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800 text-center">
                      Thank you! We'll get back to you within 24 hours.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800 text-center">
                      Something went wrong. Please try again or email us directly.
                    </p>
                  </div>
                )}
              </div>
            </form>
          </div>

          <div className="bg-gradient-to-br from-[#0F3A2E] to-[#0A2A20] p-10 lg:p-12 flex flex-col justify-between space-y-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-6">Get In Touch</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#C9A24D] rounded-full flex items-center justify-center flex-shrink-0 shadow-lg transform hover:scale-105 transition-transform">
                    <i className="ri-mail-line text-xl text-[#0F3A2E]"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-white mb-2 text-base">Email Us</h4>
                    <a href="mailto:Providencepath@pm.me" className="text-base lg:text-lg font-semibold text-[#C9A24D] hover:text-[#B8963E] cursor-pointer break-all">
                      Providencepath@pm.me
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#C9A24D] rounded-full flex items-center justify-center flex-shrink-0 shadow-lg transform hover:scale-105 transition-transform">
                    <i className="ri-phone-line text-xl text-[#0F3A2E]"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-white mb-2 text-base">Call Us</h4>
                    <a href="tel:+19207703836" className="text-base lg:text-lg font-semibold text-[#C9A24D] hover:text-[#B8963E] cursor-pointer">
                      (920) 770-3836
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#C9A24D] rounded-full flex items-center justify-center flex-shrink-0 shadow-lg transform hover:scale-105 transition-transform">
                    <i className="ri-time-line text-xl text-[#0F3A2E]"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-white mb-2 text-base">Office Hours</h4>
                    <p className="text-base text-white/90 font-medium">Monday - Friday: 9am - 5pm CST</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-4">Free Discovery Call</h3>
              <p className="text-base mb-6 text-white/90">
                Not ready to commit? Schedule a free 30-minute call to discuss your business and see if we're a good fit.
              </p>
              <a
                href="https://calendly.com/providencepath"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 bg-[#C9A24D] text-[#0F3A2E] font-bold rounded-full hover:bg-[#B8963E] transition-all whitespace-nowrap cursor-pointer shadow-lg transform hover:scale-105"
              >
                Schedule Your Call
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
